import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Website {
  id: string;
  user_id: string;
  name: string;
  url: string;
  login?: string;
  password?: string;
  api_key?: string;
  expiry_date?: string;
  category?: string;
  provider?: string;
  created_at: string;
  updated_at: string;
  // Nowe pola dla relacji z hostingami
  assigned_hostings?: string[];
  // Schema.org (publish-time only) — null OK
  schema_enabled?: boolean;
  organization_legal_name?: string | null;
  organization_description?: string | null;
  organization_logo_url?: string | null;
  organization_same_as?: string[];
  organization_founding_date?: string | null;
  // Workspace split (firma 🏢 / sideprojects 🎨) — spójne z thematic_maps.workspace
  workspace?: "firma" | "sideprojects";
  // Test połączenia WP (szkic→delete) — patrz wpConnectionTest.ts
  connection_status?: "ok" | "error" | null;
  connection_checked_at?: string | null;
  connection_error?: string | null;
  // Sync dat z AfterMarket — patrz aftermarketService.ts.
  // `not_on_account` = domeny nie ma na koncie (przepadła albo inny rejestrator).
  expiry_synced_at?: string | null;
  expiry_source?: "manual" | "aftermarket" | "not_on_account" | null;
  am_autorenew?: boolean | null;
}

export interface NewWebsite {
  name: string;
  url: string;
  login?: string;
  password?: string;
  api_key?: string;
  expiry_date?: string;
  category?: string;
  provider?: string;
  workspace?: "firma" | "sideprojects";
}

export interface WebsiteSchemaFields {
  schema_enabled?: boolean;
  organization_legal_name?: string | null;
  organization_description?: string | null;
  organization_logo_url?: string | null;
  organization_same_as?: string[];
  organization_founding_date?: string | null;
}

/** Pola metadanych wygaśnięcia — ustawiane przez sync AfterMarket albo ręczną edycję. */
export interface WebsiteExpiryFields {
  expiry_source?: "manual" | "aftermarket" | "not_on_account" | null;
  expiry_synced_at?: string | null;
  am_autorenew?: boolean | null;
}

export const useWebsites = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch websites from database
  const fetchWebsites = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('websites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWebsites(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas pobierania domen';
      setError(errorMessage);
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  // Create new website
  const createWebsite = useCallback(async (websiteData: NewWebsite): Promise<Website | null> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Convert empty strings to 'EMPTY' for database
      const processedData = {
        ...websiteData,
        login: websiteData.login || 'EMPTY',
        password: websiteData.password || 'EMPTY',
        api_key: websiteData.api_key || 'EMPTY',
        expiry_date: websiteData.expiry_date || 'EMPTY',
        provider: websiteData.provider || 'EMPTY',
        category: websiteData.category || 'EMPTY',
      };

      const { data, error } = await supabase
        .from('websites')
        .insert({
          user_id: user.id,
          ...processedData,
        })
        .select()
        .single();

      if (error) throw error;

      setWebsites(prev => [data, ...prev]);
      toast({
        title: "Sukces",
        description: "Domena została dodana",
      });

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas dodawania domeny';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  }, [user, toast]);

  // Create multiple websites (bulk)
  const createWebsitesBulk = useCallback(async (websitesData: NewWebsite[]): Promise<Website[]> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany",
        variant: "destructive",
      });
      return [];
    }

    try {
      const websitesWithUser = websitesData.map(website => ({
        user_id: user.id,
        login: website.login || 'EMPTY',
        password: website.password || 'EMPTY',
        api_key: website.api_key || 'EMPTY',
        expiry_date: website.expiry_date || 'EMPTY',
        provider: website.provider || 'EMPTY',
        category: website.category || 'EMPTY',
        name: website.name,
        url: website.url,
      }));

      const { data, error } = await supabase
        .from('websites')
        .insert(websitesWithUser)
        .select();

      if (error) throw error;

      setWebsites(prev => [...data, ...prev]);
      toast({
        title: "Sukces",
        description: `Dodano ${data.length} domen`,
      });

      return data || [];
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas dodawania domen';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return [];
    }
  }, [user, toast]);

  // Update website
  const updateWebsite = useCallback(async (id: string, updates: Partial<NewWebsite> & WebsiteSchemaFields & WebsiteExpiryFields): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('websites')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setWebsites(prev => prev.map(website =>
        website.id === id ? { ...website, ...updates } : website
      ));

      toast({
        title: "Sukces",
        description: "Domena została zaktualizowana",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas aktualizacji domeny';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  // Delete website
  const deleteWebsite = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('websites')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWebsites(prev => prev.filter(website => website.id !== id));
      toast({
        title: "Sukces",
        description: "Domena została usunięta",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas usuwania domeny';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  // Renew website expiry date (+1 year)
  const renewWebsiteExpiry = useCallback(async (id: string): Promise<boolean> => {
    try {
      const website = websites.find(w => w.id === id);
      if (!website?.expiry_date) return false;

      const currentDate = new Date(website.expiry_date);
      const newExpiryDate = new Date(currentDate);
      newExpiryDate.setFullYear(currentDate.getFullYear() + 1);

      const { error } = await supabase
        .from('websites')
        .update({
          expiry_date: newExpiryDate.toISOString().split('T')[0],
          // ręczne "+1 rok" nie jest już prawdą z AfterMarket
          expiry_source: 'manual',
        })
        .eq('id', id);

      if (error) throw error;

      setWebsites(prev => prev.map(website =>
        website.id === id
          ? { ...website, expiry_date: newExpiryDate.toISOString().split('T')[0], expiry_source: 'manual' as const }
          : website
      ));

      toast({
        title: "Sukces",
        description: "Domena została odnowiona na następny rok",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas odnawiania domeny';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [websites, toast]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    // Unikalna nazwa per mount — Phoenix dopuszcza 1 join per topic per socket;
    // dwa komponenty z useWebsites na raz zabijałyby sobie nawzajem subskrypcję.
    const subscription = supabase
      .channel(`websites_changes_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'websites',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setWebsites(prev => {
            const exists = prev.some(w => w.id === payload.new.id);
            if (!exists) {
              return [payload.new as Website, ...prev];
            }
            return prev;
          });
        } else if (payload.eventType === 'UPDATE') {
          setWebsites(prev => prev.map(w =>
            w.id === payload.new.id ? payload.new as Website : w
          ));
        } else if (payload.eventType === 'DELETE') {
          setWebsites(prev => prev.filter(w => w.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id]);

  // Initial fetch
  useEffect(() => {
    if (user?.id) {
      fetchWebsites();
    }
  }, [user?.id, fetchWebsites]);

  // Lokalny patch bez DB write — np. wynik testu połączenia (DB update robi
  // wpConnectionTest, tu tylko instant feedback w UI zanim dojdzie realtime).
  const patchWebsiteLocal = useCallback((id: string, patch: Partial<Website>) => {
    setWebsites(prev => prev.map(w => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  return {
    websites,
    loading,
    error,
    createWebsite,
    createWebsitesBulk,
    updateWebsite,
    deleteWebsite,
    renewWebsiteExpiry,
    patchWebsiteLocal,
    refreshWebsites: fetchWebsites,
  };
};
